import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Heading,
  Divider,
  VStack,
  Flex,
  Button,
  useToast,
  ButtonGroup,
  SimpleGrid,
  Grid,
  GridItem,
} from '@chakra-ui/react';

import axios from 'axios';
import { AvatarInput } from '../../../../../components/Form/AvatarInput';
import { MaskedInput } from '../../../../../components/Form/MaskedInput';
import { DefaultLayout } from '../../../_layout/DefaultLayout';
import { InternationalPhoneInput } from '../../../../../components/Form/InternationalPhoneInput';
import { createUsersService } from '../../../../../services/Users/CreateUsersService';
import { updateUserAvatarsService } from '../../../../../services/Users/UpdateUserAvatarsService';
import { translateError } from '../../../../../utils/errors';
import {
  validateCpf,
  validateBRPhone,
} from '../../../../../utils/documentValidation';
import { ReactSelect } from '../../../../../components/Form/ReactSelect';
import { DatePicker } from '../../../../../components/Form/DatePicker';
import { IAddressBase } from '../../../../../models/address';
import AddressForm, {
  addressValidation,
} from '../../../../../components/AddressForm';
import { Switch } from '../../../../../components/Form/Switch';
import { useAuth } from '../../../../../hooks/auth';

type NewLodgerFormData = {
  address?: IAddressBase;
  bio?: string;
  birthDate: Date;
  cpf?: string;
  email: string;
  healthCheck?: Date;
  name: string;
  foreignerDocument?: string;
  foreignerDocumentType?: string;
  useForeignerDocument: boolean;
  phone?: string;
};

const registerLodgerFormSchema = Yup.object().shape({
  address: addressValidation
    .nullable()
    .transform((value, originalValue) =>
      !!originalValue &&
      Object.values<string | undefined>(originalValue).some(
        (val) => !!val?.length,
      )
        ? originalValue
        : null,
    ),
  bio: Yup.string()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  birthDate: Yup.date().nullable().required('Data de nascimento requerida'),
  cpf: Yup.string().when('useForeignerDocument', {
    is: (val: boolean) => val === false,
    then: Yup.string()
      .length(11, 'Valor inválido, requerido 11 dígitos')
      .test('is-valid', 'CPF inválido', (value) => validateCpf(value))
      .nullable()
      .transform((_, originalValue) =>
        originalValue.replace(/\D/g, '').length
          ? originalValue.replace(/\D/g, '')
          : null,
      )
      .required('CPF requerido'),
    otherwise: Yup.string()
      .nullable()
      .transform(() => null),
  }),
  email: Yup.string()
    .email('E-mail inválido')
    .required('E-mail requerido')
    .transform((value) => value.toLowerCase()),
  healthCheck: Yup.date()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  useForeignerDocument: Yup.boolean().default(false),
  name: Yup.string()
    .required('Nome requerido')
    .matches(/^[a-zA-ZÀ-ÿ\s]+$/, 'Nome inválido'),
  foreignerDocument: Yup.string().when('useForeignerDocument', {
    is: (val: boolean) => val === true,
    then: Yup.string().when('foreignerDocumentType', {
      is: (val: string) => val === 'PASSPORT',
      then: Yup.string()
        .matches(/[A-Za-z0-9]$/, 'Apenas letras e números')
        .nullable()
        .transform((_, originalValue) =>
          originalValue.length ? originalValue : null,
        )
        .uppercase()
        .required('Passaporte requerido'),
      otherwise: Yup.string().when('foreignerDocumentType', {
        is: (val: string) => val === 'RNE',
        then: Yup.string()
          .matches(/[A-Za-z0-9]$/, 'Apenas letras e números')
          .nullable()
          .min(8, 'Mínimo 8 dígitos')
          .max(12, 'Máximo 12 dígitos')
          .transform((_, originalValue) =>
            originalValue.length ? originalValue : null,
          )
          .uppercase()
          .required('RNE requerido'),
        otherwise: Yup.string().when('foreignerDocumentType', {
          is: (val: string) => val === 'DRIVERS_LICENSE',
          then: Yup.string()
            .matches(/[A-Za-z0-9]$/, 'Apenas letras e números')
            .nullable()
            .transform((_, originalValue) =>
              originalValue.length ? originalValue : null,
            )
            .uppercase()
            .required('Carteira de motorista requerida'),
          otherwise: Yup.string()
            .matches(/[A-Za-z0-9]$/, 'Apenas letras e números')
            .nullable()
            .transform((_, originalValue) =>
              originalValue.length ? originalValue : null,
            )
            .uppercase()
            .required('Documento requerido'),
        }),
      }),
    }),
  }),
  foreignerDocumentType: Yup.string().when('useForeignerDocument', {
    is: (val: boolean) => val === true,
    then: Yup.string().nullable().required('Tipo do documento requerido'),
    otherwise: Yup.string()
      .nullable()
      .transform(() => null),
  }),
  phone: Yup.string()
    .test('is-valid', 'Telefone inválido', (value) => validateBRPhone(value))
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
});

export const LodgerRegister = (): JSX.Element => {
  const { goBack, push } = useHistory();

  const toast = useToast();

  const { venture: authenticatedVenture } = useAuth();

  const [avatar, setAvatar] = useState<File>();
  const [avatarUrl, setAvatarUrl] = useState<string>();

  const formMethods = useForm({
    resolver: yupResolver(registerLodgerFormSchema),
  });

  const {
    register,
    handleSubmit,
    formState,
    control,
    setValue,
    watch,
    clearErrors,
  } = formMethods;

  const { errors } = formState;

  const isForeigner = watch('useForeignerDocument');
  const foreignerDocType = watch('foreignerDocumentType');

  const foreignerDocumentTypeSelectOptions = useMemo(
    () => [
      {
        label: 'RNE',
        value: 'RNE',
      },
      {
        label: 'Passaporte',
        value: 'PASSPORT',
      },
      {
        label: 'Carteira de motorista',
        value: 'DRIVERS_LICENSE',
      },
      {
        label: 'Outros',
        value: 'OTHERS',
      },
    ],
    [],
  );

  useEffect(() => {
    if (isForeigner) {
      setValue('cpf', '');
    }

    if (!isForeigner) {
      setValue('foreignerDocument', '');
      setValue('foreignerDocumentType', '');
    }
  }, [isForeigner, setValue]);

  useEffect(() => {
    clearErrors('foreignerDocument');
  }, [foreignerDocType, clearErrors]);

  const handleChangeAvatar = (file: File): void => {
    setAvatar(file);
    setAvatarUrl(URL.createObjectURL(file));
  };

  const handleDeleteAvatar = (): void => {
    setAvatar(undefined);
    setAvatarUrl(undefined);
  };

  const handleNewLodger: SubmitHandler<NewLodgerFormData> = useCallback(
    async ({
      address,
      bio,
      birthDate,
      cpf,
      email,
      healthCheck,
      name,
      foreignerDocument,
      foreignerDocumentType,
      useForeignerDocument,
      phone,
    }) => {
      try {
        const lodger = await createUsersService({
          address,
          bio,
          birthDate,
          cpf,
          email,
          featureGroupKey: 'LODGER',
          healthCheck,
          name,
          foreignerDocument,
          foreignerDocumentType,
          useForeignerDocument,
          phone,
          venturesId: authenticatedVenture?.id
            ? [authenticatedVenture?.id]
            : [],
        });

        if (avatar) {
          const formData = new FormData();

          formData.append('avatar', avatar);

          await updateUserAvatarsService({
            avatarData: formData,
            userId: lodger.id,
          });
        }

        toast({
          title: 'Cadastrado com sucesso',
          description: 'O hóspede foi cadastrado corretamente.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });

        push('/lodgers/details', { lodgerId: lodger.id });
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Falha no cadastro',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao cadastrar o  hóspede, tente novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    },
    [authenticatedVenture?.id, avatar, push, toast],
  );

  return (
    <DefaultLayout>
      <FormProvider {...formMethods}>
        <Box
          as="form"
          flex="1"
          borderRadius={8}
          bg="white"
          p="8"
          onSubmit={handleSubmit(handleNewLodger)}
        >
          <Heading size="lg" fontWeight="normal">
            Cadastrar hóspede
          </Heading>

          <Divider my="6" borderColor="gray.300" />

          <Flex direction="column" align="center" mb="8">
            <AvatarInput
              avatarUrl={avatarUrl}
              onChange={handleChangeAvatar}
              onDelete={handleDeleteAvatar}
              showWebcamButton
            />
          </Flex>

          <VStack spacing="8">
            <MaskedInput
              label="Nome completo"
              error={errors.name}
              {...register('name')}
            />

            <Grid gap="4" templateColumns="repeat(12, 1fr)" w="full">
              <GridItem
                colSpan={!isForeigner ? [12, 8, 9, 9, 6] : [12, 12, 12, 9, 10]}
              >
                {!isForeigner ? (
                  <MaskedInput
                    label="CPF"
                    mask="cpf"
                    error={errors.cpf}
                    {...register('cpf')}
                  />
                ) : (
                  <Grid
                    templateColumns="repeat(12, 1fr)"
                    gap={[6, 6, 0]}
                    w="full"
                  >
                    <GridItem colSpan={[12, 12, 5]}>
                      <ReactSelect
                        label="Tipo"
                        name="foreignerDocumentType"
                        options={foreignerDocumentTypeSelectOptions}
                        control={control}
                        error={errors.foreignerDocumentType}
                      />
                    </GridItem>

                    <GridItem colSpan={[12, 12, 7]}>
                      <MaskedInput
                        label="Documento"
                        error={errors.foreignerDocument}
                        {...register('foreignerDocument')}
                      />
                    </GridItem>
                  </Grid>
                )}
              </GridItem>

              <GridItem
                colSpan={!isForeigner ? [12, 4, 3, 3, 2] : [12, 12, 12, 3, 2]}
              >
                <Switch
                  label="Usar documento internacional"
                  error={errors.useForeignerDocument}
                  {...register('useForeignerDocument')}
                />
              </GridItem>
            </Grid>

            <SimpleGrid minChildWidth="240px" spacing="6" w="full">
              <InternationalPhoneInput
                flex={1}
                label="Telefone"
                name="phone"
                control={control}
                error={errors.phone}
              />

              <MaskedInput
                label="E-mail"
                type="email"
                textTransform="lowercase"
                error={errors.email}
                {...register('email')}
              />
            </SimpleGrid>

            <SimpleGrid minChildWidth="240px" spacing="6" w="full">
              <DatePicker
                label="Data de nascimento"
                isClearable={false}
                maxDate={new Date()}
                control={control}
                error={errors.birthDate}
                {...register('birthDate')}
              />

              <DatePicker
                label="Exame médico"
                maxDate={new Date()}
                control={control}
                error={errors.healthCheck}
                {...register('healthCheck')}
              />
            </SimpleGrid>

            <MaskedInput
              label="Biografia"
              as="textarea"
              minHeight="160px"
              resize="none"
              py="2"
              error={errors.bio}
              {...register('bio')}
            />
          </VStack>

          <Divider my="6" borderColor="gray.300" />

          <AddressForm />

          <Flex mt="12" justify="flex-end">
            <ButtonGroup>
              <Button colorScheme="blackAlpha" onClick={goBack}>
                Cancelar
              </Button>
              <Button
                type="submit"
                colorScheme="green"
                isLoading={formState.isSubmitting}
              >
                Salvar
              </Button>
            </ButtonGroup>
          </Flex>
        </Box>
      </FormProvider>
    </DefaultLayout>
  );
};
